import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  CalendarDaysIcon, 
  PaperAirplaneIcon,
  ClockIcon,
  XMarkIcon,
  CheckCircleIcon,
  PlusIcon,
  TrashIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8005';

export default function MessageScheduler() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [scheduledMessages, setScheduledMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  
  // Single schedule form
  const [scheduleForm, setScheduleForm] = useState({
    message_id: '',
    recipient_id: '',
    scheduled_date: '',
    scheduled_time: '07:00',
    delivery_method: 'whatsapp'
  });
  
  // Bulk schedule form
  const [bulkForm, setBulkForm] = useState({
    recipient_ids: [],
    start_date: '',
    frequency: 'daily',
    selected_messages: []
  });

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [messagesRes, recipientsRes, scheduledRes] = await Promise.all([
        axios.get(`${API_URL}/recordings/user/${user.id}`),
        axios.get(`${API_URL}/recipients/user/${user.id}`),
        axios.get(`${API_URL}/schedule/messages/${user.id}`)
      ]);

      setMessages(messagesRes.data.recordings || []);
      setRecipients(recipientsRes.data.recipients || []);
      setScheduledMessages(scheduledRes.data.scheduled_messages || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleSingle = async (e) => {
    e.preventDefault();
    
    try {
      const scheduledDateTime = `${scheduleForm.scheduled_date}T${scheduleForm.scheduled_time}:00`;
      
      await axios.post(`${API_URL}/schedule/message`, {
        message_id: scheduleForm.message_id,
        recipient_id: scheduleForm.recipient_id,
        scheduled_date: scheduledDateTime,
        delivery_method: scheduleForm.delivery_method
      });

      alert('✅ Message scheduled successfully!');
      setShowScheduleModal(false);
      setScheduleForm({
        message_id: '',
        recipient_id: '',
        scheduled_date: '',
        scheduled_time: '07:00',
        delivery_method: 'whatsapp'
      });
      fetchData();
    } catch (error) {
      console.error('Failed to schedule message:', error);
      alert('❌ Failed to schedule message. Please try again.');
    }
  };

  const handleBulkSchedule = async (e) => {
    e.preventDefault();
    
    try {
      const messagesWithOffset = bulkForm.selected_messages.map((msgId, index) => ({
        message_id: msgId,
        day_offset: index
      }));

      await axios.post(`${API_URL}/schedule/deployment-bulk`, {
        user_id: user.id,
        recipient_ids: bulkForm.recipient_ids,
        messages: messagesWithOffset,
        start_date: `${bulkForm.start_date}T07:00:00`,
        frequency: bulkForm.frequency
      });

      alert(`✅ Scheduled ${messagesWithOffset.length} messages for ${bulkForm.recipient_ids.length} recipient(s)!`);
      setShowBulkModal(false);
      setBulkForm({
        recipient_ids: [],
        start_date: '',
        frequency: 'daily',
        selected_messages: []
      });
      fetchData();
    } catch (error) {
      console.error('Failed to bulk schedule:', error);
      alert('❌ Failed to schedule messages. Please try again.');
    }
  };

  const handleCancelScheduled = async (scheduledId) => {
    if (!window.confirm('Are you sure you want to cancel this scheduled message?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/schedule/${scheduledId}`);
      alert('✅ Scheduled message cancelled');
      fetchData();
    } catch (error) {
      console.error('Failed to cancel:', error);
      alert('❌ Failed to cancel message');
    }
  };

  const toggleRecipient = (recipientId) => {
    setBulkForm(prev => ({
      ...prev,
      recipient_ids: prev.recipient_ids.includes(recipientId)
        ? prev.recipient_ids.filter(id => id !== recipientId)
        : [...prev.recipient_ids, recipientId]
    }));
  };

  const toggleMessage = (messageId) => {
    setBulkForm(prev => ({
      ...prev,
      selected_messages: prev.selected_messages.includes(messageId)
        ? prev.selected_messages.filter(id => id !== messageId)
        : [...prev.selected_messages, messageId]
    }));
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      sent: 'bg-green-100 text-green-800 border-green-200',
      failed: 'bg-red-100 text-red-800 border-red-200',
      cancelled: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return badges[status] || badges.pending;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <ArrowPathIcon className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading scheduler...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 mb-8 text-white shadow-2xl">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold mb-2">📅 Message Scheduler</h1>
            <p className="text-blue-100 text-lg">
              Schedule messages for deployment or set up recurring delivery
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowScheduleModal(true)}
              className="px-6 py-3 bg-white text-blue-600 rounded-xl font-semibold hover:bg-blue-50 transition-all shadow-lg flex items-center gap-2"
            >
              <ClockIcon className="h-5 w-5" />
              Schedule Single
            </button>
            <button
              onClick={() => setShowBulkModal(true)}
              className="px-6 py-3 bg-yellow-400 text-blue-900 rounded-xl font-semibold hover:bg-yellow-300 transition-all shadow-lg flex items-center gap-2"
            >
              <CalendarDaysIcon className="h-5 w-5" />
              Deployment Wizard
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-500">Total Scheduled</span>
            <CalendarDaysIcon className="h-5 w-5 text-blue-500" />
          </div>
          <div className="text-3xl font-bold text-gray-900">
            {scheduledMessages.filter(m => m.status === 'pending').length}
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-500">Sent</span>
            <CheckCircleIcon className="h-5 w-5 text-green-500" />
          </div>
          <div className="text-3xl font-bold text-gray-900">
            {scheduledMessages.filter(m => m.status === 'sent').length}
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-500">Available Messages</span>
            <PaperAirplaneIcon className="h-5 w-5 text-purple-500" />
          </div>
          <div className="text-3xl font-bold text-gray-900">{messages.length}</div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-500">Recipients</span>
            <PaperAirplaneIcon className="h-5 w-5 text-orange-500" />
          </div>
          <div className="text-3xl font-bold text-gray-900">{recipients.length}</div>
        </div>
      </div>

      {/* Scheduled Messages List */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Scheduled Messages</h2>
          <p className="text-sm text-gray-600 mt-1">
            {scheduledMessages.length} total • {scheduledMessages.filter(m => m.status === 'pending').length} pending
          </p>
        </div>

        {scheduledMessages.length === 0 ? (
          <div className="p-12 text-center">
            <CalendarDaysIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Scheduled Messages</h3>
            <p className="text-gray-600 mb-6">
              Get started by scheduling your first message or use the Deployment Wizard
            </p>
            <button
              onClick={() => setShowBulkModal(true)}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-all"
            >
              Start Deployment Wizard
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {scheduledMessages.map((scheduled) => (
              <div key={scheduled.scheduled_id} className="p-6 hover:bg-gray-50 transition-all">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {scheduled.message_title}
                      </h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusBadge(scheduled.status)}`}>
                        {scheduled.status.toUpperCase()}
                      </span>
                    </div>
                    <div className="flex items-center gap-6 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <CalendarDaysIcon className="h-4 w-4" />
                        <span>{new Date(scheduled.scheduled_date).toLocaleString()}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <PaperAirplaneIcon className="h-4 w-4" />
                        <span>To: {scheduled.recipient_name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                          {scheduled.delivery_method}
                        </span>
                      </div>
                    </div>
                    {scheduled.metadata?.deployment_schedule && (
                      <div className="mt-2 text-xs text-blue-600 font-medium">
                        🎖️ Deployment Day {scheduled.metadata.day_number}
                      </div>
                    )}
                  </div>
                  {scheduled.status === 'pending' && (
                    <button
                      onClick={() => handleCancelScheduled(scheduled.scheduled_id)}
                      className="ml-4 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all"
                      title="Cancel scheduled message"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Single Schedule Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold">Schedule Single Message</h2>
                <button
                  onClick={() => setShowScheduleModal(false)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-all"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            <form onSubmit={handleScheduleSingle} className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Select Message
                </label>
                <select
                  value={scheduleForm.message_id}
                  onChange={(e) => setScheduleForm({...scheduleForm, message_id: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">Choose a message...</option>
                  {messages.map((msg) => (
                    <option key={msg._id} value={msg._id}>
                      {msg.title || msg.transcription?.substring(0, 50) || 'Untitled Message'}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Select Recipient
                </label>
                <select
                  value={scheduleForm.recipient_id}
                  onChange={(e) => setScheduleForm({...scheduleForm, recipient_id: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">Choose a recipient...</option>
                  {recipients.map((recipient) => (
                    <option key={recipient._id} value={recipient._id}>
                      {recipient.name} ({recipient.relationship})
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Date
                  </label>
                  <input
                    type="date"
                    value={scheduleForm.scheduled_date}
                    onChange={(e) => setScheduleForm({...scheduleForm, scheduled_date: e.target.value})}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Time
                  </label>
                  <input
                    type="time"
                    value={scheduleForm.scheduled_time}
                    onChange={(e) => setScheduleForm({...scheduleForm, scheduled_time: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Delivery Method
                </label>
                <select
                  value={scheduleForm.delivery_method}
                  onChange={(e) => setScheduleForm({...scheduleForm, delivery_method: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="whatsapp">WhatsApp</option>
                  <option value="sms">SMS</option>
                  <option value="email">Email</option>
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowScheduleModal(false)}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-all shadow-lg"
                >
                  Schedule Message
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Schedule Modal (Deployment Wizard) */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="bg-gradient-to-r from-yellow-400 to-orange-500 p-6 text-blue-900">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold">🎖️ Deployment Wizard</h2>
                  <p className="text-blue-800 mt-1">Schedule multiple messages for your deployment period</p>
                </div>
                <button
                  onClick={() => setShowBulkModal(false)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-all"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            <form onSubmit={handleBulkSchedule} className="p-6 space-y-6">
              {/* Step 1: Select Recipients */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Step 1: Who should receive messages?</h3>
                <div className="grid grid-cols-2 gap-3">
                  {recipients.map((recipient) => (
                    <label
                      key={recipient._id}
                      className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        bulkForm.recipient_ids.includes(recipient._id)
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={bulkForm.recipient_ids.includes(recipient._id)}
                        onChange={() => toggleRecipient(recipient._id)}
                        className="h-5 w-5 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <div className="ml-3">
                        <div className="font-semibold text-gray-900">{recipient.name}</div>
                        <div className="text-sm text-gray-500">{recipient.relationship}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Step 2: Select Messages */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Step 2: Select messages to send</h3>
                <p className="text-sm text-gray-600 mb-3">Messages will be sent in order, one per day</p>
                <div className="max-h-64 overflow-y-auto space-y-2 border border-gray-200 rounded-lg p-3">
                  {messages.map((msg, index) => (
                    <label
                      key={msg._id}
                      className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all ${
                        bulkForm.selected_messages.includes(msg._id)
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={bulkForm.selected_messages.includes(msg._id)}
                        onChange={() => toggleMessage(msg._id)}
                        className="h-5 w-5 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <div className="ml-3 flex-1">
                        <div className="font-medium text-gray-900">
                          Day {bulkForm.selected_messages.indexOf(msg._id) + 1 || index + 1}: {msg.title || 'Untitled'}
                        </div>
                        <div className="text-sm text-gray-500 truncate">
                          {msg.transcription?.substring(0, 60)}...
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Step 3: Schedule Settings */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Deployment Start Date
                  </label>
                  <input
                    type="date"
                    value={bulkForm.start_date}
                    onChange={(e) => setBulkForm({...bulkForm, start_date: e.target.value})}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Frequency
                  </label>
                  <select
                    value={bulkForm.frequency}
                    onChange={(e) => setBulkForm({...bulkForm, frequency: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="daily">Daily (one message per day)</option>
                    <option value="weekly">Weekly (one message per week)</option>
                  </select>
                </div>
              </div>

              {/* Summary */}
              {bulkForm.recipient_ids.length > 0 && bulkForm.selected_messages.length > 0 && (
                <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
                  <h4 className="font-bold text-blue-900 mb-2">📊 Deployment Summary</h4>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-blue-600 font-medium">Recipients</div>
                      <div className="text-2xl font-bold text-blue-900">{bulkForm.recipient_ids.length}</div>
                    </div>
                    <div>
                      <div className="text-blue-600 font-medium">Messages</div>
                      <div className="text-2xl font-bold text-blue-900">{bulkForm.selected_messages.length}</div>
                    </div>
                    <div>
                      <div className="text-blue-600 font-medium">Total Scheduled</div>
                      <div className="text-2xl font-bold text-blue-900">
                        {bulkForm.recipient_ids.length * bulkForm.selected_messages.length}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowBulkModal(false)}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={bulkForm.recipient_ids.length === 0 || bulkForm.selected_messages.length === 0}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Schedule {bulkForm.recipient_ids.length * bulkForm.selected_messages.length} Messages
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
